import React, { useEffect, useState } from "react";
import { Tabs, Tab } from "@mui/material";
import {
  NotificationProvider,
  PermissionCenterProvider,
  AuthLayout,
  DarkModeProvider,
} from "@1823-partners/core";

import api, { fetchAvailableDates } from "./api";
import Main from "./components/Main";
import Ratings from "./components/Ratings";


/*
 * MUI Chip bug workaround, applied at the theme so it also covers chips this app does not render.
 * Chip's root slot wires up
 *   onClick: event => { handlers.onClick?.(event); onClick(event); }
 * with NO guard on the second call, so clicking a Chip that was given no handler throws
 * "onClick is not a function" and takes the tab down.
 *
 * The chips that hit this are the Granted/Missing labels inside core's permissions drawer, which
 * cannot be patched from here -- the drawer ships compiled inside @1823-partners/core. MUI applies
 * defaultProps to every instance of a component, including ones rendered from another bundle, so
 * supplying the no-op here reaches them.
 *
 * clickable:false is the other half and is not redundant: MUI computes
 *   clickable = clickableProp !== false && onClick ? true : clickableProp
 * so the no-op alone would promote every chip in the app to a ButtonBase with a ripple and a
 * pointer cursor. This app has no genuinely clickable chip; one added later must pass clickable
 * explicitly.
 *
 * Module scope, not inline: DarkModeProvider memoises the theme on this object's identity.
 */
const THEME_OPTIONS = {
  components: {
    MuiChip: { defaultProps: { clickable: false, onClick: () => {} } },
  },
};

export default function App() {
  const [availableDates, setAvailableDates] = useState([]);
  const [asof, setAsof] = useState(""); // "YYYY-MM-DD"
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Fetch available dates once on mount
  useEffect(() => {
    (async () => {
      try {
        const datesResp = await fetchAvailableDates();
        setAvailableDates(datesResp.data || []);
        if (datesResp.data && datesResp.data.length > 0) {
          const mostRecent = datesResp.data.sort().reverse()[0];
          setAsof(mostRecent);
        }
      } catch (error) {
        console.error("Error fetching available dates:", error);
      }
    })();
  }, []);

  const tabsHeader = (
    <Tabs
      value={currentTab}
      onChange={handleTabChange}
      textColor="inherit"
      sx={{ flexGrow: 1 }}
    >
      <Tab label="Dashboard" />
      <Tab label="Weekly Internal Spreads" />
    </Tabs>
  );

  return (
    <DarkModeProvider appName="alm" themeOptions={THEME_OPTIONS}>
      <NotificationProvider>
        {/* Mounts the permissions drawer (the toolbar's shield button) and, because it wraps
            PermissionRequestProvider itself, keeps turning the "Request access" button on
            permission-denied toasts into an in-app request. Do not also mount
            PermissionRequestProvider -- that nests a second copy. Inside NotificationProvider
            because the request path reports its outcome as a toast. */}
        <PermissionCenterProvider api={api} appName="alm">
          <AuthLayout
            title="ALM Dashboard"
            headerContent={tabsHeader}
            contentSx={{}}
          >
            {({ userInfo }) => (
              <>
                {currentTab === 0 && (
                  <Main availableDates={availableDates} asof={asof} setAsof={setAsof} />
                )}
                {currentTab === 1 && (
                  <Ratings user={userInfo?.userDetails} />
                )}
              </>
            )}
          </AuthLayout>
        </PermissionCenterProvider>
      </NotificationProvider>
    </DarkModeProvider>
  );
}
